import BattleEnemy from './modules/BattleEnemy.js';
import BattlePlayer from './modules/BattlePlayer.js';
import BattleAnimation from './modules/BattleAnimation.js';
import BattleUI from './modules/BattleUI.js';
import BattleLogic from './modules/BattleLogic.js';
import BattleReward from './modules/BattleReward.js';
import ItemSystem from './modules/ItemSystem.js';
import SettingsHelper from './modules/SettingsHelper.js';
import DebugHelper from './modules/DebugHelper.js';
import ErrorHandler from './modules/ErrorHandler.js';
import MessageHelper from './modules/MessageHelper.js';
import BattleActionHandler from './modules/Battle/BattleActionHandler.js';
import BattleTurnManager from './modules/Battle/BattleTurnManager.js';
import BattleStatusManager from './modules/Battle/BattleStatusManager.js';
import PlayerDefeatHandler from './modules/PlayerDefeatHandler.js';
import GameStateStore from './modules/GameStateStore.js';
import AutoSaveManager from './modules/AutoSaveManager.js';
import { getWorldScene } from './modules/SceneRegistry.js';
import { GAME_CONSTANTS } from './modules/GameConstants.js';
import { ensureBattleAssets, playSceneBgm } from './modules/LazyAssetLoader.js';

export default class BattleScene extends Phaser.Scene {
    constructor() {
        super('BattleScene');
    }

    init(data) {
        try {
            this.battleEnemy = new BattleEnemy(this);
            this.battlePlayer = new BattlePlayer(this);
            this.battleAnimation = new BattleAnimation(this);
            this.battleUI = new BattleUI(this);
            this.battleLogic = new BattleLogic(this);
            this.battleReward = new BattleReward(this);
            
            this.battleData = data || {};
            
            if (data) {
                this.battleLogic.init(data);
                if (data.player) {
                    this.battlePlayer.init(data.player);
                }
                if (data.enemies) {
                    this.battleEnemy.init(data.enemies);
                }
            } else {
                DebugHelper.warn('BattleScene: 没有战斗数据');
            }
            
            this.battleState = {
                phase: 'playerTurn',
                isAnimating: false,
                selectedTarget: null,
                rangeAttackMode: false,
                selectedSkill: null,
                selectedItem: null,
                selectedWeapon: null,
                selectedWeaponSlot: null,
                isTeamBattle: false
            };
            
            this.isTeamBattle = false;
            this.teamMembers = [];
            this.currentTeamMemberIndex = 0;
            this.teamBattleData = null;
            this.battleEnded = false; // 标记战斗是否已结束
            
            if (data && data.isTeamBattle) {
                this.isTeamBattle = true;
                this.teamMembers = data.teamMembers || [];
                this.teamBattleData = data.teamBattleData || {};
                
                DebugHelper.log('战斗场景初始化 - 组队模式:', {
                    teamMembersCount: this.teamMembers.length,
                    teamMembers: this.teamMembers.map(m => ({
                        playerId: m.playerId,
                        playerName: m.playerName,
                        hasPlayerData: !!m.playerData
                    }))
                });
                
                // 确保每个队员都有自己的玩家数据
                // 如果是队员收到战斗消息，需要获取自己的数据
                if (this.teamMembers.length > 0 && window.networkManager) {
                    const localPlayerId = window.networkManager.playerId;
                    const localPlayer = GameStateStore.getPlayer();
                    this.teamMembers.forEach(member => {
                        if (member.playerId === localPlayerId && localPlayer) {
                            member.playerData = localPlayer;
                        } else if (!member.playerData) {
                            member.playerData = localPlayer || data.player;
                        }
                    });
                }
                
                DebugHelper.log('战斗场景初始化 - 处理后的队伍成员:', {
                    teamMembersCount: this.teamMembers.length,
                    teamMembers: this.teamMembers.map(m => ({
                        playerId: m.playerId,
                        playerName: m.playerName,
                        hasPlayerData: !!m.playerData,
                        playerDataKeys: m.playerData ? Object.keys(m.playerData) : []
                    }))
                });
            } else if (GameStateStore.isMultiplayerConnected()) {
                const worldScene = getWorldScene(
                    this.scene,
                    this.battleData?.previousSceneKey
                );
                if (worldScene && worldScene.teamManager && worldScene.teamManager.getTeamId()) {
                    this.isTeamBattle = true;
                    const teamMembers = worldScene.teamManager.getTeamMembers();
                    const localPlayer = GameStateStore.getPlayer();
                    this.teamMembers = teamMembers.map(member => ({
                        playerId: member.playerId,
                        playerName: member.playerName,
                        playerData: localPlayer
                    }));
                }
            }
            
            this.bgm = null;
        } catch (error) {
            ErrorHandler.handleError(error, '战斗系统初始化', this);
            this.scene.start('WorldScene');
        }
    }

    create() {
        ensureBattleAssets(this, this.battleData)
            .then(() => this.buildBattle())
            .catch((error) => {
                ErrorHandler.handleError(error, '战斗资源加载', this);
                this.exitBattleAfterLoadFailure();
            });
    }

    exitBattleAfterLoadFailure() {
        const previousScene = this.scene.manager.getScenes(true).find(
            (s) => s.scene.key !== 'BattleScene' && s.scene.isPaused()
        );
        if (previousScene) {
            previousScene.movementEnabled = true;
            this.scene.stop();
            this.scene.resume(previousScene.scene.key);
        } else {
            this.scene.start('WorldScene');
        }
    }

    buildBattle() {
        try {
            this.createBattleBackground();
            
            this.battleAnimation.create();
            this.battleEnemy.create();
            this.battlePlayer.create();
            this.battleUI.create();
            
            this.battleActionHandler = new BattleActionHandler(
                this,
                this.battlePlayer,
                this.battleEnemy,
                this.battleAnimation,
                this.battleUI,
                this.battleLogic,
                this.battleState
            );
            this.battleTurnManager = new BattleTurnManager(
                this,
                this.battlePlayer,
                this.battleEnemy,
                this.battleAnimation,
                this.battleUI,
                this.battleLogic,
                this.battleState,
                this.battleReward
            );
            this.battleStatusManager = new BattleStatusManager(
                this,
                this.battlePlayer,
                this.battleEnemy,
                this.battleAnimation,
                this.battleLogic
            );
            
            this.setupEventListeners();
            this.setupNetworkListeners();
            this.setupCamera();
            this.startPlayerTurn();
            this.playBattleMusic();
        } catch (error) {
            ErrorHandler.handleError(error, '战斗场景创建', this);
            const previousScene = this.scene.manager.getScenes(true).find(s => s.scene.key !== 'BattleScene' && s.scene.isPaused());
            if (previousScene) {
                previousScene.movementEnabled = true;
                this.scene.stop();
                this.scene.resume(previousScene.scene.key);
            } else {
                this.scene.start('WorldScene');
            }
        }
    }

    createBattleBackground() {
        this.cameras.main.setBackgroundColor('#222222');
    }

    setupCamera() {
        // 确保相机可见
        this.cameras.main.setVisible(true);
        this.cameras.main.fadeIn(500);
    }

    playBattleMusic() {
        const volume = SettingsHelper.getBGMPlayVolume();
        const bgmKey = this.battleData?.bgm || 'battle_bgm';
        playSceneBgm(this, bgmKey, { volume });
    }

    setupEventListeners() {
        // UI事件
        this.battleUI.events.on('actionSelected', (action) => {
            this.handleAction(action);
        });
        
        this.battleUI.events.on('targetSelected', (targetIndex, isRangeAttack) => {
            this.handleTargetSelection(targetIndex, isRangeAttack);
        });
        
        this.battleUI.events.on('cancelTargetSelection', () => {
            this.cancelTargetSelection();
        });
        
        this.battleUI.events.on('escapeAttempt', () => {
            this.handleEscapeAttempt();
        });
        
        this.battleUI.events.on('skillSelected', (skill, skillIndex) => {
            this.handleSkillSelection(skill, skillIndex);
        });
        
        this.battleUI.events.on('cancelSkillSelection', () => {
            this.cancelTargetSelection();
        });
        
        this.battleUI.events.on('itemSelected', (item, itemIndex) => {
            this.handleItemSelection(item, itemIndex);
        });
        
        this.battleUI.events.on('cancelItemSelection', () => {
            this.cancelItemSelection();
        });
        
        this.battleUI.events.on('weaponSelected', (weapon, weaponIndex, weaponType) => {
            this.handleWeaponSelection(weapon, weaponIndex, weaponType);
        });
        
        this.battleUI.events.on('cancelWeaponSelection', () => {
            this.cancelWeaponSelection();
        });
        
        // 强制下车事件（SP为0时触发）
        this.events.on('forceExitVehicle', (onComplete) => {
            this.handleExitVehicle(onComplete);
        });

        this.events.on('playerStunnedSkipTurn', () => {
            this.time.delayedCall(700, () => this.startEnemyTurn());
        });
    }
    
    /**
     * 设置网络监听器（组队模式）
     */
    setupNetworkListeners() {
        if (!this.isTeamBattle || !window.networkManager) {
            return;
        }

        this._networkUnsubscribers = [];

        const networkManager = window.networkManager;

        const onBattleTurn = (payload) => {
            if (payload && typeof payload.teamMemberIndex === 'number') {
                DebugHelper.log('收到战斗回合切换消息，更新队员索引:', payload.teamMemberIndex);
                this.currentTeamMemberIndex = payload.teamMemberIndex;
                this.startPlayerTurn();
            }
        };

        const onBattleAction = (payload) => {
            if (!payload || !payload.action) {
                return;
            }
            
            DebugHelper.log('收到战斗动作消息:', payload);
            
            const localPlayerId = networkManager ? networkManager.playerId : null;
            const currentMember = this.teamMembers[this.currentTeamMemberIndex];
            if (!currentMember || currentMember.playerId === localPlayerId) {
                DebugHelper.log('是自己的动作，跳过同步');
                return;
            }
            
            this.executeSyncBattleAction(payload);
        };

        const onBattleVictory = (payload) => {
            DebugHelper.log('收到战斗胜利消息，显示奖励窗口');
            if (this.battleEnded) {
                DebugHelper.log('战斗已结束，忽略胜利消息');
                return;
            }
            this.battleEnded = true;
            this.handleVictoryFromNetwork();
        };

        const onBattleDefeat = (payload) => {
            DebugHelper.log('收到战斗失败消息');
            if (this.battleEnded) {
                DebugHelper.log('战斗已结束，忽略失败消息');
                return;
            }
            this.battleEnded = true;
            this.handleDefeat();
        };

        this._networkUnsubscribers.push(networkManager.on('battleTurn', onBattleTurn));
        this._networkUnsubscribers.push(networkManager.on('battleAction', onBattleAction));
        this._networkUnsubscribers.push(networkManager.on('battleVictory', onBattleVictory));
        this._networkUnsubscribers.push(networkManager.on('battleDefeat', onBattleDefeat));
    }

    clearNetworkListeners() {
        if (this._networkUnsubscribers) {
            this._networkUnsubscribers.forEach((unsubscribe) => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            });
            this._networkUnsubscribers = null;
        }
    }
    
    /**
     * 执行同步的战斗动作（来自其他客户端）
     * @param {Object} actionData - 动作数据
     */
    executeSyncBattleAction(actionData) {
        DebugHelper.log('执行同步战斗动作:', actionData);
        
        switch (actionData.action) {
            case 'attack':
                if (actionData.actionType === 'single') {
                    // 找到当前操作的队员并切换到他的显示
                    const currentMember = this.teamMembers[this.currentTeamMemberIndex];
                    if (currentMember && currentMember.playerData) {
                        // 更新当前操作的队员数据
                        this.battlePlayer.init(currentMember.playerData);
                        this.battlePlayer.switchToMember(this.currentTeamMemberIndex);
                    }
                    
                    const target = this.battleEnemy.getEnemyByIndex(actionData.targetIndex);
                    if (target && target.visible) {
                        // 构造同步数据
                        const syncData = {
                            damage: {
                                damage: actionData.damage,
                                isCritical: actionData.isCritical || false,
                                weapon: actionData.weaponId ? { id: actionData.weaponId, name: actionData.weaponId } : null
                            },
                            animationConfig: actionData.animationConfig
                        };
                        this.battleActionHandler.executeSingleAttack(target, null, syncData);
                    }
                } else if (actionData.actionType === 'range') {
                    // 范围攻击
                    const currentMember = this.teamMembers[this.currentTeamMemberIndex];
                    if (currentMember && currentMember.playerData) {
                        this.battlePlayer.init(currentMember.playerData);
                        this.battlePlayer.switchToMember(this.currentTeamMemberIndex);
                    }
                    
                    const targets = actionData.targetIndices.map(index => 
                        this.battleEnemy.getEnemyByIndex(index)
                    ).filter(t => t && t.visible);
                    if (targets.length > 0) {
                        const syncData = {
                            damage: actionData.damage,
                            targetDamages: actionData.targetDamages
                        };
                        // 范围攻击需要特殊处理
                        this.battleActionHandler.executeRangeAttack(targets, null, syncData);
                    }
                }
                break;
                
            case 'defense':
                // 找到当前操作的队员
                const currentMember = this.teamMembers[this.currentTeamMemberIndex];
                if (currentMember && currentMember.playerData) {
                    // 更新当前操作的队员数据
                    this.battlePlayer.init(currentMember.playerData);
                    this.battlePlayer.switchToMember(this.currentTeamMemberIndex);
                }
                const player = this.battlePlayer.getPlayer();
                this.battleActionHandler.executeDefense(player, null, { sync: true });
                break;
                
            case 'skill':
                if (actionData.skillType === 'attack') {
                    const target = this.battleEnemy.getEnemyByIndex(actionData.targetIndex);
                    if (target && target.visible) {
                        const skill = { name: actionData.skillName, id: actionData.skillId };
                        const syncData = {
                            damage: actionData.damage,
                            skillEffect: actionData.skillEffect
                        };
                        this.battleActionHandler.executeSkillAttack(skill, target, null, syncData);
                    }
                } else if (actionData.skillType === 'heal') {
                    const skill = { name: actionData.skillName, id: actionData.skillId };
                    const syncData = {
                        heal: actionData.heal
                    };
                    this.battleActionHandler.executeHealSkill(skill, null, syncData);
                }
                break;
                
            case 'item':
                // 道具使用同步
                break;
                
            default:
                DebugHelper.warn('未知的战斗动作类型:', actionData.action);
        }
    }

    handleAction(action) {
        if (action === '逃跑' || action === '撤退') {
            // 逃跑由回合管理器处理
            this.battleTurnManager.handleEscapeAttempt(
                () => this.exitBattle(null, { escaped: true }),
                () => {
                    // 逃跑失败后，开始敌人回合
                    this.startEnemyTurn();
                }
            );
        } else if (action === '防御') {
            // 防御：执行防御后结束回合
            this.handleDefense();
        } else if (action === '上车') {
            // 上车：执行上车后结束回合
            this.handleBoardVehicle();
        } else if (action === '下车') {
            // 下车：执行下车后结束回合
            this.handleExitVehicle();
        } else {
            // 其他行动由行动处理器处理
            this.battleActionHandler.handleAction(
                action,
                this.isTeamBattle,
                this.teamMembers,
                this.currentTeamMemberIndex
            );
        }
    }
    
    handleBoardVehicle() {
        this.battleActionHandler.executeBoardVehicle(() => {
            // 组队模式：检查是否需要切换到下一个队员
            if (this.isTeamBattle && this.currentTeamMemberIndex < this.teamMembers.length - 1) {
                const newIndex = this.currentTeamMemberIndex + 1;
                this.currentTeamMemberIndex = newIndex;
                // 通知其他客户端切换到下一个队员
                if (window.networkManager && window.networkManager.isConnected()) {
                    window.networkManager.notifyTeamBattleTurn(newIndex);
                }
                this.startPlayerTurn();
            } else {
                this.startEnemyTurn();
            }
        });
    }
    
    handleExitVehicle() {
        this.battleActionHandler.executeExitVehicle(() => {
            // 组队模式：检查是否需要切换到下一个队员
            if (this.isTeamBattle && this.currentTeamMemberIndex < this.teamMembers.length - 1) {
                const newIndex = this.currentTeamMemberIndex + 1;
                this.currentTeamMemberIndex = newIndex;
                // 通知其他客户端切换到下一个队员
                if (window.networkManager && window.networkManager.isConnected()) {
                    window.networkManager.notifyTeamBattleTurn(newIndex);
                }
                this.startPlayerTurn();
            } else {
                this.startEnemyTurn();
            }
        });
    }
    
    handleDefense() {
        const player = this.battlePlayer.getPlayer();
        this.battleActionHandler.executeDefense(player, () => {
            // 组队模式：检查是否需要切换到下一个队员
            if (this.isTeamBattle && this.currentTeamMemberIndex < this.teamMembers.length - 1) {
                const newIndex = this.currentTeamMemberIndex + 1;
                this.currentTeamMemberIndex = newIndex;
                // 通知其他客户端切换到下一个队员
                if (window.networkManager && window.networkManager.isConnected()) {
                    window.networkManager.notifyTeamBattleTurn(newIndex);
                }
                this.startPlayerTurn();
            } else {
                this.startEnemyTurn();
            }
        });
    }

    startAttack() {
        this.battleActionHandler.startAttack();
    }

    handleTargetSelection(targetIndex, isRangeAttack = false) {
        this.battleActionHandler.handleTargetSelection(
            targetIndex,
            isRangeAttack,
            () => this.checkBattleStatusAfterAttack()
        );
    }

    // executeSingleAttack 和 executeRangeAttack 已移至 BattleActionHandler

    checkBattleStatusAfterAttack() {
        this.battleTurnManager.checkBattleStatusAfterAttack(
            this.isTeamBattle,
            this.teamMembers,
            this.currentTeamMemberIndex,
            () => this.handleVictory(),
            () => this.handleDefeat(),
            (newIndex) => {
                this.currentTeamMemberIndex = newIndex;
                // 组队模式：通知其他客户端切换到下一个队员
                if (this.isTeamBattle && window.networkManager && window.networkManager.isConnected()) {
                    window.networkManager.notifyTeamBattleTurn(newIndex);
                }
                this.startPlayerTurn();
            },
            () => this.startEnemyTurn() // 添加敌人回合回调
        );
    }

    startEnemyTurn() {
        this.battleTurnManager.startEnemyTurn(
            () => this.processEnemyStatusEffects(),
            () => this.handleDefeat(),
            () => {
                // 让所有敌人依次攻击
                const aliveEnemies = this.battleEnemy.getAliveEnemies();
                
                if (aliveEnemies.length === 0) {
                    this.startPlayerTurn();
                    return;
                }
                
                // 使用对象来存储计数器，避免闭包问题
                const attackState = {
                    completedAttacks: 0,
                    totalAttacks: aliveEnemies.length
                };
                
                // 让每个敌人都攻击一次
                aliveEnemies.forEach((enemy, index) => {
                    this.time.delayedCall(index * GAME_CONSTANTS.BATTLE.ENEMY_ATTACK_STAGGER_MS, () => {
                        this.battleLogic.executeEnemyTurn(
                            [enemy], // 只传入当前敌人
                            this.battlePlayer.getPlayer(),
                            (attacker, damage) => {
                                this.battleAnimation.playEnemyAttackAnimation(
                                    attacker,
                                    this.battlePlayer.playerSprite,
                                    damage,
                                    () => {
                                        const result = this.battlePlayer.applyDamage(damage);
                                        this.battleAnimation.showDamage(this.battlePlayer.playerSprite, damage);
                                        
                                        // SP为0时强制下车
                                        if (result && result.shouldExitVehicle) {
                                            this.battleUI.showMessage('战车SP耗尽，强制下车！', '#ff0000');
                                            this.handleExitVehicle(() => {
                                                // 下车后继续处理攻击
                                                attackState.completedAttacks++;
                                                if (attackState.completedAttacks >= attackState.totalAttacks) {
                                                    if (this.battlePlayer.isDefeated()) {
                                                        this.handleDefeat();
                                                    } else {
                                                        this.startPlayerTurn();
                                                    }
                                                }
                                            });
                                            return;
                                        }
                                        
                                        attackState.completedAttacks++;
                                        
                                        // 检查是否所有敌人攻击完成
                                        if (attackState.completedAttacks >= attackState.totalAttacks) {
                                            // 所有敌人攻击完成，检查玩家是否被击败
                                            if (this.battlePlayer.isDefeated()) {
                                                this.handleDefeat();
                                            } else {
                                                // 切换到玩家回合
                                                this.startPlayerTurn();
                                            }
                                        }
                                    }
                                );
                            }
                        );
                    });
                });
            }
        );
    }
    
    processEnemyStatusEffects() {
        this.battleStatusManager.processEnemyStatusEffects(
            () => this.handleVictory()
        );
    }

    handleVictory() {
        // 如果战斗已经结束，不再处理
        if (this.battleEnded) {
            DebugHelper.log('战斗已结束，忽略胜利处理');
            return;
        }
        
        // 标记战斗已结束，避免重复处理
        this.battleEnded = true;
        
        // 组队模式：发送胜利消息到服务器（如果不是从网络接收的）
        if (this.isTeamBattle && window.networkManager && window.networkManager.isConnected()) {
            const defeatedEnemies = this.battleEnemy.getDefeatedEnemies();
            window.networkManager.notifyTeamBattleVictory({
                defeatedEnemies: defeatedEnemies.map(e => ({
                    id: e.id,
                    name: e.name,
                    exp: e.exp,
                    money: e.money
                }))
            });
        }
        
        this.battleTurnManager.handleVictory(
            this.bgm,
            (victory, rewards) => this.exitBattle(victory, rewards)
        );
    }
    
    /**
     * 从网络接收的胜利消息处理（避免重复发送网络消息）
     */
    handleVictoryFromNetwork() {
        // 标记战斗已结束
        this.battleEnded = true;
        
        // 直接处理胜利，不发送网络消息
        this.battleTurnManager.handleVictory(
            this.bgm,
            (victory, rewards) => this.exitBattle(victory, rewards)
        );
    }

    handleDefeat() {
        // 如果战斗已经结束，不再处理
        if (this.battleEnded) {
            DebugHelper.log('战斗已结束，忽略失败处理');
            return;
        }
        
        // 标记战斗已结束，避免重复处理
        this.battleEnded = true;
        
        // 组队模式：发送失败消息到服务器（如果不是从网络接收的）
        if (this.isTeamBattle && window.networkManager && window.networkManager.isConnected()) {
            window.networkManager.notifyTeamBattleDefeat();
        }
        
        this.battleTurnManager.handleDefeat(
            (defeat) => this.exitBattle(defeat)
        );
    }

    handleEscapeAttempt() {
        this.battleTurnManager.handleEscapeAttempt(
            () => this.exitBattle(null, { escaped: true }),
            () => {
                // 逃跑失败后，开始敌人回合
                // startEnemyTurn 内部会在敌人攻击完成后自动调用 startPlayerTurn
                this.startEnemyTurn();
            }
        );
    }

    cancelTargetSelection() {
        this.battleActionHandler.cancelTargetSelection();
    }

    startPlayerTurn() {
        // 组队模式：检查是否所有队员都完成了回合
        if (this.isTeamBattle && this.currentTeamMemberIndex >= this.teamMembers.length) {
            // 所有队员都完成了回合，进入敌人回合
            this.currentTeamMemberIndex = 0;
            this.startEnemyTurn();
            return;
        }
        
        // 组队模式：切换到当前队员并更新显示
        if (this.isTeamBattle && this.currentTeamMemberIndex < this.teamMembers.length) {
            const currentMember = this.teamMembers[this.currentTeamMemberIndex];
            if (currentMember && currentMember.playerData) {
                // 更新当前操作的队员数据
                this.battlePlayer.init(currentMember.playerData);
                // 切换到当前队员的显示
                this.battlePlayer.switchToMember(this.currentTeamMemberIndex);
            }
        }
        
        this.battleTurnManager.startPlayerTurn(
            this.isTeamBattle,
            this.teamMembers,
            this.currentTeamMemberIndex,
            () => this.processPlayerStatusEffects()
        );
    }
    
    processPlayerStatusEffects() {
        this.battleStatusManager.processPlayerStatusEffects(
            () => this.handleDefeat()
        );
    }
    
    startSkillSelection() {
        this.battleActionHandler.startSkillSelection();
    }
    
    handleSkillSelection(skill, skillIndex) {
        this.battleActionHandler.handleSkillSelection(
            skill,
            skillIndex,
            this.isTeamBattle,
            this.teamMembers,
            this.currentTeamMemberIndex
        );
    }
    
    executeHealSkill(skill) {
        this.battleActionHandler.executeHealSkill(skill, () => {
            // 组队模式：检查是否需要切换到下一个队员
            if (this.isTeamBattle && this.currentTeamMemberIndex < this.teamMembers.length - 1) {
                const newIndex = this.currentTeamMemberIndex + 1;
                this.currentTeamMemberIndex = newIndex;
                // 通知其他客户端切换到下一个队员
                if (window.networkManager && window.networkManager.isConnected()) {
                    window.networkManager.notifyTeamBattleTurn(newIndex);
                }
                this.startPlayerTurn();
            } else {
                this.startEnemyTurn();
            }
        });
    }
    
    executeSkillAttack(skill, target) {
        this.battleActionHandler.executeSkillAttack(skill, target, () => this.checkBattleStatusAfterAttack());
    }
    
    cancelSkillSelection() {
        this.battleActionHandler.cancelSkillSelection();
    }
    
    startItemSelection() {
        this.battleActionHandler.startItemSelection();
    }
    
    handleItemSelection(item, itemIndex) {
        this.battleActionHandler.handleItemSelection(item, itemIndex, () => {
            // 组队模式：检查是否需要切换到下一个队员
            if (this.isTeamBattle && this.currentTeamMemberIndex < this.teamMembers.length - 1) {
                const newIndex = this.currentTeamMemberIndex + 1;
                this.currentTeamMemberIndex = newIndex;
                // 通知其他客户端切换到下一个队员
                if (window.networkManager && window.networkManager.isConnected()) {
                    window.networkManager.notifyTeamBattleTurn(newIndex);
                }
                this.startPlayerTurn();
            } else {
                this.startEnemyTurn();
            }
        });
    }
    
    executeItemUse(item) {
        this.battleActionHandler.executeItemUse(item, () => {
            // 组队模式：检查是否需要切换到下一个队员
            if (this.isTeamBattle && this.currentTeamMemberIndex < this.teamMembers.length - 1) {
                const newIndex = this.currentTeamMemberIndex + 1;
                this.currentTeamMemberIndex = newIndex;
                // 通知其他客户端切换到下一个队员
                if (window.networkManager && window.networkManager.isConnected()) {
                    window.networkManager.notifyTeamBattleTurn(newIndex);
                }
                this.startPlayerTurn();
            } else {
                this.startEnemyTurn();
            }
        });
    }
    
    executeItemAttack(item, target) {
        this.battleActionHandler.executeItemAttack(item, target, () => this.checkBattleStatusAfterAttack());
    }
    
    cancelItemSelection() {
        this.battleActionHandler.cancelItemSelection();
    }
    
    handleWeaponSelection(weapon, weaponIndex, weaponType) {
        this.battleActionHandler.handleWeaponSelection(weapon, weaponIndex, weaponType);
    }
    
    cancelWeaponSelection() {
        this.battleActionHandler.cancelWeaponSelection();
    }

    
    // BattleScene.js - 修改 exitBattle 方法
    exitBattle(victory = false, rewards = null) {
        this.clearNetworkListeners();
        
        if (victory) {
            AutoSaveManager.onBattleVictory();
        }
        
        if (this.bgm) {
            this.bgm.stop();
            this.bgm = null;
        }
        
        // 获取战斗后的玩家数据
        const playerData = this.battlePlayer.getPlayer();
        DebugHelper.log('战斗后玩家数据:', playerData);
        
        // 战斗失败时传送到城镇出生点
        if (!victory && playerData && playerData.hp <= 0) {
            DebugHelper.log('战斗失败，玩家HP为0，传送到城镇出生点');
            const previousScene = this.battleData?.previousSceneKey || 
                                  (() => {
                                      const pausedScenes = this.scene.manager.getScenes(true).filter(s => s.scene.isPaused());
                                      return pausedScenes.length > 0 ? pausedScenes[0].scene.key : null;
                                  })();
            PlayerDefeatHandler.handlePlayerDefeated(this, previousScene);
            return; // 直接返回，不执行后续的恢复场景逻辑
        }
        
        // 同步战斗后的玩家数据到 gameState（保留位置和静态信息）
        const gameStatePlayer = GameStateStore.getPlayer();
        if (gameStatePlayer && playerData) {
            const originalX = gameStatePlayer.x;
            const originalY = gameStatePlayer.y;
            const originalTileX = gameStatePlayer.tileX;
            const originalTileY = gameStatePlayer.tileY;
            
            GameStateStore.syncFromScene(playerData, {
                includeVehicle: true,
                includePosition: false
            });
            
            gameStatePlayer.x = originalX;
            gameStatePlayer.y = originalY;
            gameStatePlayer.tileX = originalTileX;
            gameStatePlayer.tileY = originalTileY;
        }
        
        const previousScene = this.battleData?.previousSceneKey || 
                              (() => {
                                  const pausedScenes = this.scene.manager.getScenes(true).filter(s => s.scene.isPaused());
                                  return pausedScenes.length > 0 ? pausedScenes[0].scene.key : 'WorldScene';
                              })();
        
        this.scene.stop();
        
        const sceneToResume = this.scene.get(previousScene);
        if (sceneToResume) {
            sceneToResume.movementEnabled = true;
            this.scene.resume(previousScene, {
                from: 'BattleScene',
                player: playerData,
                victory: victory,
                rewards: rewards
            });
            
            if (sceneToResume.cameras && sceneToResume.cameras.main) {
                sceneToResume.cameras.main.setVisible(true);
            }
            
            if (sceneToResume.worldInteraction) {
                sceneToResume.worldInteraction.resetBattleTrigger();
            }

            if (sceneToResume.worldUI?.isDialogActive) {
                sceneToResume.worldUI.hideDialog();
            }
            
            this.time.delayedCall(100, () => {
                const resumedScene = this.scene.get(previousScene);
                if (resumedScene && resumedScene.scene && resumedScene.scene.isActive() && resumedScene.cameras && resumedScene.cameras.main) {
                    resumedScene.cameras.main.setVisible(true);
                }
                if (resumedScene && resumedScene.worldInteraction) {
                    resumedScene.worldInteraction.resetBattleTrigger();
                }
            });
        } else {
            DebugHelper.warn(`BattleScene: 未找到要恢复的场景: ${previousScene}，使用默认场景`);
            this.scene.start('WorldScene', {
                from: 'BattleScene',
                player: playerData,
                victory: victory,
                rewards: rewards
            });
        }
    }




    update(time, delta) {
        this.battleUI.update(time, delta);
        this.battleAnimation.update(time, delta);
    }
}